import { useMemo, useState, useEffect } from 'react';

import moment from 'moment';

import { useAppContext } from 'services/context';
import { useToggle, useTranslation } from 'services/hooks';

import { COURSE_TYPES, MEETING_TYPES, NOTIFICATION_TYPES } from 'constants/index.js';
import Appointment from 'components/Calendar/Appointment';
import Button from 'components/Button';
import Container from 'components/Container';
import Calendar from 'components/Calendar';
import DatePicker from 'components/Input/DatePicker';
import Input from 'components/Input';
import Icon from 'components/Icon';
import Select from 'components/Input/Select';
import TextArea from 'components/Input/TextArea';
import Table from 'pages/ManagementPanel/Table';
import Modal from 'components/Modal';
import MultiSelect, { Checkbox } from 'components/Input/MultiSelect';
import { capitalizeFirstLetter, getDateValues, validateDates, validateMeetingValues } from 'services/utils';

import './style.css';

const ClassesManagement = () => {
	const { Api, addNotification } = useAppContext();
	const getTranslation = useTranslation();

	const TRANSLATION_KEY = 'classes_management';
	const VALIDATIONS_TRANSLATION_KEY = 'validations';

	// FORM RESULTS
	const [searchResults, setSearchResults] = useState();
	// DATE PICKER ACTIVE STATE
	const [currentDate, setCurrentDate] = useState(undefined);
	// STUDENTS  IN CLASS OR BANK STATES
	const [studentsInClass, setStudentsInClass] = useState(undefined);
	const [studentsBank, setStudentsBank] = useState(undefined);
	// UPCOMING APPOINTMENTS
	const [upcomingAppointments, setUpcomingAppointments] = useState([]);
	// ACTIVE CLASS
	const [currentClass, setCurrentClass] = useState(undefined);

	// CALENDAR MEETINGS RESULTS
	const [calendarMeetings, setCalendarMeetings] = useState([]);
	//POPUP BUTTONS DISABLE STATE
	const [popupDidSendMassageButtonDisabled, setPopupDidSendMassageButtonDisabled] = useState(false);
	const [popupAppointmentsDeleteDisabled, setPopupAppointmentsDeleteDisabled] = useState(false);

	const [saveMeetingBtnDisabled, setSaveMeetingBtnDisabled] = useState(false);

	const [sendClassUpdate, setSendClassUpdate] = useState(false);

	// MODALS STATES
	const [createClassModal, toggleCreateClassModal] = useToggle(false);
	const [studentModal, toggleStudentModal] = useToggle(false);
	const [appointmentsModal, toggleAppointmentsModal] = useToggle(false);

	// DDLS AND FILTERS FORM
	const [ddls, setDdls] = useState({});
	const [searchFormData, setSearchFormData] = useState({
		classId: '',
		courseId: '',
		classStatusId: '',
		classFromDate: '',
		classToDate: '',
	});

	// CLASSES TABLE HEADERS
	const classesTableHeaders = useMemo(() => {
		return [
			{
				Header: ' ',
				columns: [
					{
						//running number - rows counter
						Header: '#',
						// accessor: 'userId',
						Cell: row => <>{row?.row.index + 1}</>,
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_course_column`),
						accessor: 'courseName',
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_date_column`),
						accessor: 'createdDate',
						//sort by date
						sortType: (a, b) => {
							return (
								new Date(moment(a.original.createdDate, 'DD-MM-YYYY hh:mm').format('MM-DD-YYYY  hh:mm')) -
								new Date(moment(b.original.createdDate, 'DD-MM-YYYY hh:mm').format('MM-DD-YYYY  hh:mm'))
							);
						},
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_class_column`),
						accessor: 'className',
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_status_column`),
						accessor: 'classStatusName',
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_number_students_column`),
						accessor: 'classStudentsCount',
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_students_column`),
						Cell: row => (
							<div
								className="flex"
								title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_students_column_title`)}
								onClick={() => _handleStudentCellClick(row)}
							>
								<Icon icon="userIcon" />
							</div>
						),
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_meetings_column`),
						Cell: row => (
							<div
								className="flex"
								title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_meetings_column_title`)}
								onClick={() => _handleAppointmentsCellClick(row)}
							>
								<Icon icon="calendarIcon" />
							</div>
						),
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_edit_column`),
						Cell: row => (
							<div
								className="flex"
								title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_edit_column`)}
								onClick={() => _handleClassCellClick(row)}
							>
								<Icon icon="editIcon2" />
							</div>
						),
					},
				],
			},
		];
	}, [getTranslation]);

	// UPCOMING APPOINTMENTS TABLE HEADERS
	const upcomingAppointmentsTableHeaders = useMemo(() => {
		return [
			{
				Header: ' ',
				columns: [
					{
						Header: '#', //running number - rows counter
						Cell: row => (
							<> {`${getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_appointments_session_column`)} ${row?.row?.index + 1}`}</>
						),
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_appointments_date_column`),
						accessor: row => (row?.meetingDateTimeFrom ? `${row?.meetingDateTimeFrom}` : ` ${row?.startTime} ${row?.date}`),
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_appointments_remark_column`),
						accessor: 'meetingRemark',
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_appointments_delete_column`),
						accessor: row => (
							<div
								className="flex"
								title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_appointments_delete_column_title`)}
								disabled={row.meetingId}
								onClick={() => !popupAppointmentsDeleteDisabled && _handleDeleteLocalClassMeeting(row)}
							>
								<Icon icon="trashIcon" />
							</div>
						),
					},
				],
			},
		];
	}, [popupAppointmentsDeleteDisabled, getTranslation]);

	//DDLS SEARCH FORM Memoized options values
	const sortUtil = (a, b) => (a.description > b.description ? 1 : -1);

	const searchFormCourseNamesOptions = useMemo(() => {
		let options = ddls?.courses?.options?.filter(option => option.courseTypeId === COURSE_TYPES.IELTS);
		options = options?.sort(sortUtil);
		return { ...ddls.courses, options };
	}, [ddls]);
	const classStatusesOptions = useMemo(() => {
		let options = ddls?.classStatus?.options?.filter(option => {
			try {
				//check if courseId exist on searchFormCourseNamesOptions
				return searchFormCourseNamesOptions?.options?.some(courseName => courseName.value === option.courseId);
			} catch (error) {
				console.error(NOTIFICATION_TYPES.ERROR, error);
				return option.courseId === parseInt(searchFormData.courseId);
			}
		});
		options = options?.sort(sortUtil);
		return { ...ddls.classStatus, options };
	}, [ddls, searchFormData.courseId, searchFormCourseNamesOptions]);
	const courseClassesOptions = useMemo(() => {
		if (!searchFormData.courseId) return ddls.classes;
		let options = ddls?.classes?.options?.filter(option => option.courseId === parseInt(searchFormData.courseId));
		options = options?.sort(sortUtil);
		return { ...ddls.classes, options };
	}, [ddls, searchFormData.courseId]);
	const courseTeachersOptions = useMemo(() => {
		let options = ddls?.courseTeachers?.options.filter(teacher => teacher.courseId === 5);
		options = options?.sort(sortUtil);
		return { ...ddls.courseTeachers, options };
	}, [ddls]);

	// GET DDLS ON LOAD
	const getDDLS = async () => {
		const _ddls = await Api.GetAllManagementDDLS();
		if (_ddls) setDdls(_ddls);
	};

	//EFFECTS
	useEffect(() => {
		getDDLS();
	}, []);
	useEffect(() => {
		if (appointmentsModal && currentClass) {
			(async () => {
				const _meetings = await Api.GetAllCoursesMeetings(currentClass.classId);
				if (!_meetings) return;
				const { upcomings, allMeetings } = _meetings;
				setCalendarMeetings(allMeetings || []);
				setUpcomingAppointments(upcomings || []);
				setPopupAppointmentsDeleteDisabled(upcomings?.length >= 8);
				//if he have already 8 meetings - will disable "Save Meeting" button
				setSaveMeetingBtnDisabled(upcomingAppointments?.length >= 8);
			})();
		} else {
			setCalendarMeetings([]);
			setUpcomingAppointments([]);
			setPopupAppointmentsDeleteDisabled(false);
			setSaveMeetingBtnDisabled(false);
		}
	}, [appointmentsModal]);

	//////HANDLE FUNCTIONS
	////CELL CLICK CALLBACKS
	const _handleStudentCellClick = async row => {
		const { original } = row?.cell?.row || {};
		if (!original) return;
		const { classId, courseId } = original;
		setCurrentClass(original);
		const students = await Api.GetNewClassAvailableStudents(classId, courseId);
		if (!students) {
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_students_click_error_message`), NOTIFICATION_TYPES.ERROR, 8000);

			return;
		}
		const _studentsBank = students
			.filter(st => !st.isInClass)
			.map(({ recordId, mainPhonecodeCountry, email }) => ({ id: recordId, description: `${mainPhonecodeCountry} - ${email}` }));
		const _inClassStudents = students
			.filter(st => st.isInClass)
			.map(({ recordId, mainPhonecodeCountry, email }) => ({ id: recordId, description: `${mainPhonecodeCountry} - ${email}` }));

		setPopupDidSendMassageButtonDisabled(_inClassStudents?.length === 0); //disabeld if there are no student in class on start
		setSaveMeetingBtnDisabled(upcomingAppointments?.length >= 8); //if he have already 8 meetings - will disable "Save Meeting" button
		setStudentsBank(_studentsBank);
		setStudentsInClass(_inClassStudents);
		toggleStudentModal();
	};
	const _handleAppointmentsCellClick = async row => {
		const { original } = row?.cell?.row || {};
		setCurrentClass(original);
		toggleAppointmentsModal();
		if (upcomingAppointments?.length >= 8) {
			setSaveMeetingBtnDisabled(true);
		}
	};
	const _handleClassCellClick = async row => {
		const { original } = row?.cell?.row || {};
		setCurrentClass(original);
		toggleCreateClassModal();
	};

	// OPEN CREATE CLASS MODAL
	const _handleOpenCreateClassModal = () => {
		toggleCreateClassModal();
		setCurrentClass();
	};

	// HANDLE CLOSE MODALS
	const _handleCloseCreateClassModal = () => {
		toggleCreateClassModal();
		setCurrentClass();
	};

	const _handleCloseAppointmentsModal = () => {
		toggleAppointmentsModal();
		setCurrentClass();
	};
	const _handleCloseStudentModal = () => {
		toggleStudentModal();
		setCurrentClass();
	};

	// SEARCH CLASSES SEARCH SUBMIT BUTTON HANDLER
	const _handleSearchSubmit = async e => {
		try {
			if (e !== undefined) {
				//clear results on real user search
				e?.preventDefault();
				setSearchResults(undefined);
			}

			const data = { ...searchFormData };
			for (const key in data) {
				if (data.hasOwnProperty(key)) {
					data[key] = data[key]?.trim().replace(/ {2}/g, ' '); // remove all extra whitespaces
					if (!data[key]) data[key] = null; // replace empty strings to null
				}
			}
			const res = await Api.SearchClasses(data);
			if (res && res.length > 0) setSearchResults(res);
			else setSearchResults([]);
		} catch (error) {
			console.warn(error);
		}
	};

	// GENERIC HANDLER FOR INPUTS
	const _handleChangeInput = (value, key) => {
		const _searchFormData = { ...searchFormData };
		_searchFormData[key] = value;
		setSearchFormData(_searchFormData);
	};

	//CLASS CRUD
	const _handleInsertUpdateClass = async e => {
		e?.preventDefault();
		const form = new FormData(e.target);
		const classData = Object.fromEntries(form.entries());
		for (const key in classData) {
			if (!classData[key]) {
				addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_insert_class_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
				return;
			}
		}

		//HANDLE NEW CLASS IS CREATED
		if (currentClass) {
			const { classId } = currentClass;
			if (classId > 0) classData.classId = classId;
		}
		const { fromDate, toDate } = classData;
		const { valid, message } = validateDates(fromDate, toDate);

		if (!valid) {
			addNotification(message, NOTIFICATION_TYPES.ERROR, 8000);

			return;
		}
		const res = await Api.InsertUpdateClass(classData);

		if (res?.myClass) {
			const RES_MESSAGE = classData.classId
				? getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_update_class_success_message`)
				: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_insert_class_success_message`);
			_handleCloseCreateClassModal();
			addNotification(RES_MESSAGE, NOTIFICATION_TYPES.SUCCESS, 8000);

			const { myClass } = res;
			setSearchResults([{ ...myClass }]);
			await getDDLS();
		} else
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_update_class_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
	};

	//STUDENT CRUD
	const _handleUpdateStudentInClass = async e => {
		e?.preventDefault();
		const form = new FormData(e.target);
		const updateStudentsForm = Object.fromEntries(form.entries());
		if (!studentsInClass || studentsInClass?.length <= 0) {
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_no_students_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
			return;
		}
		const { classId, className } = currentClass;
		const { messageToStudents, didSendMassage } = updateStudentsForm;

		const recordIds = studentsInClass.map(student => student.id); // const studentsString = studentsInClass.reduce((acc, { id }) => acc + id + ',', '');
		const response = await Api.AssignStudentsToClass(classId, recordIds, messageToStudents, Boolean(didSendMassage));
		if (response) {
			addNotification(
				`${getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_assign_students_success_message`)} - ${className}`,
				NOTIFICATION_TYPES.SUCCESS,
				8000
			);

			toggleStudentModal();
			_handleSearchSubmit();
		} else {
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_assign_students_error_message`), NOTIFICATION_TYPES.ERROR, 8000);

			console.log('DATA OF ASSIGNED STUDENTS', { currentClass, updateStudentsForm, recordIds });
		}
	};

	//HANDLE STATE SPECIFIC MEETING
	const _handleAddLocalClassMeeting = async e => {
		e?.preventDefault();
		const form = new FormData(e.target);
		const meeting = Object.fromEntries(form.entries());

		for (const key in meeting) {
			if (!meeting[key]) {
				addNotification(
					getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_add_meeting_error_message`) + ' ' + capitalizeFirstLetter(key),
					NOTIFICATION_TYPES.ERROR,
					8000
				);

				return;
			}
		}

		const _meetingDate = currentDate.format('DD-MM-YYYY');

		//The student's teacher meetings on the same day
		const calendarMeetingsFiltered = calendarMeetings.filter(
			({ teacherUserId, meetingDateTimeFrom }) =>
				teacherUserId === currentClass.classTeacherUserId && meetingDateTimeFrom.split(' ')[0] === _meetingDate //In IELTS there is one teacher for all classes
		);

		//The new meeting the customer wants to schedule + The student upcoming meetings + The student's teacher meetings on the same day
		const takenMeetings = [
			{ startTime: null, endTime: null }, // Validation to the new meeting,
			...upcomingAppointments.filter(({ date }) => date === _meetingDate),
			...calendarMeetingsFiltered.map(({ meetingDateTimeFrom, meetingDateTimeTill }) => {
				return { startTime: meetingDateTimeFrom.split(' ')[1], endTime: meetingDateTimeTill.split(' ')[1] };
			}),
		];

		const existOnUpcoming = takenMeetings.some(({ startTime, endTime }) => {
			const meetingDetails = { ...meeting, date: _meetingDate };
			const { valid, message, translationkey } = validateMeetingValues(startTime, endTime, meetingDetails);
			if (!valid) {
				addNotification(
					getTranslation(VALIDATIONS_TRANSLATION_KEY, translationkey)
						?.replace('[startTime]', meetingDetails?.startTime)
						?.replace('[endTime]', meetingDetails?.endTime) || message,
					NOTIFICATION_TYPES.ERROR,
					8000
				);

				return true;
			}
			return false;
		});
		if (existOnUpcoming) return;

		meeting.date = _meetingDate;

		setUpcomingAppointments(_upcomingAppointments => {
			if (!Array.isArray(_upcomingAppointments)) return [];
			return [..._upcomingAppointments, meeting];
		});
		setSaveMeetingBtnDisabled(upcomingAppointments?.length >= 8);
	};

	const _handleDeleteLocalClassMeeting = meeting => {
		const { startTime, endTime, date } = meeting;

		setUpcomingAppointments(_upcomingAppointments => {
			if (!Array.isArray(_upcomingAppointments)) return [];
			return _upcomingAppointments.filter(
				_upcoming => !(startTime === _upcoming.startTime && endTime === _upcoming.endTime && date === _upcoming.date)
			);
		});
		setSaveMeetingBtnDisabled(upcomingAppointments?.length >= 8);
	};

	const _handleDeleteMeeting = async meetingId => {
		const isDelete = await Api.DeleteCourseClassMeeting(meetingId);
		if (isDelete) {
			addNotification(
				getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_delete_meeting_success_message`),
				NOTIFICATION_TYPES.SUCCESS,
				8000
			);

			const _meetings = await Api.GetAllCoursesMeetings(currentClass.classId);
			setCalendarMeetings(_meetings.allMeetings || []);
			setUpcomingAppointments(_meetings.upcomings);
		} else
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_delete_meeting_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
	};

	const _handleUpdateMeeting = async meeting => {
		if (!meeting) {
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_no_meeting_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
			return false;
		}

		const { valid, message, translationkey } = validateMeetingValues(null, null, meeting);

		if (!valid) {
			addNotification(
				getTranslation(VALIDATIONS_TRANSLATION_KEY, translationkey)
					?.replace('[startTime]', meeting?.startTime)
					?.replace('[endTime]', meeting?.endTime) || message,
				NOTIFICATION_TYPES.ERROR,
				8000
			);
			return false;
		}

		const { classId } = currentClass;
		meeting.classId = classId;
		const { isUpdate, available } = await Api.UpdateCourseClassMeeting(meeting);
		if (!available) {
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_date_meeting_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
			return false;
		}
		if (isUpdate) {
			addNotification(
				getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_update_meeting_success_message`),
				NOTIFICATION_TYPES.SUCCESS,
				8000
			);

			const _meetings = await Api.GetAllCoursesMeetings(currentClass.classId);
			setCalendarMeetings(_meetings.allMeetings || []);
			setUpcomingAppointments(_meetings.upcomings);
			return true;
		}
		addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_update_meeting_error_message`), NOTIFICATION_TYPES.ERROR, 8000);

		console.warn('meeting', meeting);
		return false;
	};

	const _handleInsertMeetings = async () => {
		try {
			if (upcomingAppointments?.length !== 8) {
				addNotification(
					getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_insert_meeting_validation_error_message`),
					NOTIFICATION_TYPES.ERROR,
					8000
				);

				return;
			}
			setSaveMeetingBtnDisabled(upcomingAppointments?.length >= 8); //if he have already 8 meetings - will disable "Save Meeting" button
			const meetingsToInsert = upcomingAppointments?.filter(app => !app.meetingId);
			if (!meetingsToInsert) return;
			const { classId } = currentClass;

			const { isInsert, available, message, meetings, allMeetings } = await Api.InsertCourseClassMeeting(
				classId,
				meetingsToInsert,
				sendClassUpdate
			);

			if (!isInsert || !available) {
				addNotification(getTranslation(TRANSLATION_KEY, message), NOTIFICATION_TYPES.ERROR, 8000);
				return;
			}

			addNotification(
				getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_insert_meeting_success_message`),
				NOTIFICATION_TYPES.SUCCESS,
				8000
			);

			setUpcomingAppointments(meetings);
			setCalendarMeetings(allMeetings);
			setPopupAppointmentsDeleteDisabled(upcomingAppointments.length >= 8);
			setSendClassUpdate(false);
		} catch (error) {
			console.warn(error);
		}
	};

	// RENDER FUNCTIONS
	const _renderAppointment = (
		{ meetingDateTimeFrom, meetingDateTimeTill, meetingId, meetingRemark, meetingType, className, teacherName },
		idx
	) => {
		const dayOfMonth = moment(meetingDateTimeFrom, 'DD-MM-YYYY');
		if (!currentDate || !moment.isMoment(currentDate)) return;
		const currentDay = currentDate.isSame(dayOfMonth, 'day');
		if (!currentDay) return null;
		const [startHoursAndMinutes, startDate] = getDateValues(meetingDateTimeFrom);
		const [endHoursAndMinutes] = getDateValues(meetingDateTimeTill);

		// const title = meetingType === MEETING_TYPES.TEACHER_COURSE_CLASS_MEETINGS ? `${teacherName} meeting` : `Class ${className} meeting`;
		const title = `${teacherName} ${getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_appointment_title`)}`;

		return (
			<Appointment
				timeFrom={startHoursAndMinutes || getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_appointment_time_from`)}
				timeTo={endHoursAndMinutes || getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_appointment_time_to`)}
				title={title}
				key={idx}
				meetingId={meetingId}
				onDelete={_handleDeleteMeeting}
				onUpdate={_handleUpdateMeeting}
				meetingRemark={`${getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_appointment_meeting_remark`)} ${className || ''} - ${
					meetingRemark || ''
				}`}
				remarkDefaultValue={meetingRemark}
				date={startDate || '-'}
				allowEdit={meetingType === MEETING_TYPES.TEACHER_COURSE_CLASS_MEETINGS || meetingType === MEETING_TYPES.USER_COURSE_CLASS_MEETINGS}
			/>
		);
	};

	return (
		<div className="ClassesManagement">
			<Container>
				<div className="ClassesManagement__title">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_title`)}</div>
				<Button
					title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_button`)}
					small
					endIcon="plusIcon"
					onClick={_handleOpenCreateClassModal}
				/>

				<div className="wrapper">
					<div className="ClassesManagement__inputs_grid">
						<Select
							placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_course_field`)}
							label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_course_field`)}
							name={searchFormCourseNamesOptions?.name}
							options={searchFormCourseNamesOptions?.options || []}
							onChange={({ target }) => _handleChangeInput(target.value || null, 'courseId')}
						/>

						<Select
							placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_class_field`)}
							label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_class_field`)}
							name={courseClassesOptions?.name}
							options={courseClassesOptions?.options || []}
							onChange={({ target }) => _handleChangeInput(target.value || null, 'classId')}
						/>

						<Select
							placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_status_field`)}
							label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_status_field`)}
							name={classStatusesOptions?.name}
							options={classStatusesOptions?.options || []}
							onChange={({ target }) => _handleChangeInput(target.value || null, 'classStatusId')}
						/>

						<DatePicker
							fromTo
							label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_date_field`)}
							startDateName="startDate"
							endDateName="endDate"
							onFromChange={value => _handleChangeInput(value, 'classFromDate')}
							onToChange={value => _handleChangeInput(value, 'classToDate')}
						/>
					</div>
					<Button
						title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_button`)}
						endIcon="searchIcon"
						small
						onClick={_handleSearchSubmit}
					/>
					{searchResults && (
						<div className="wrapper">
							{searchResults?.length === 0 && (
								<h2 className="no_records">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_search_no_results`)}</h2>
							)}
							{searchResults?.length > 0 && <Table columns={classesTableHeaders} data={searchResults} />}
						</div>
					)}
				</div>
			</Container>

			<Modal isVisible={createClassModal} extraClassName="ClassModal" setVisibility={_handleCloseCreateClassModal}>
				<div className="ClassModal__title">
					{currentClass
						? `${getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_class_modal_update_title`)} ${currentClass.className}`
						: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_class_modal_create_title`)}
				</div>
				<form className="ClassModal__wrapper" onSubmit={_handleInsertUpdateClass}>
					<Input
						label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_class_modal_class_field`)}
						name="className"
						defaultValue={currentClass?.className || ''}
					/>
					<Select
						label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_class_modal_status_field`)}
						name="classStatusId"
						options={classStatusesOptions?.options || []}
						defaultValue={currentClass?.classStatusId || 1}
					/>
					<Select
						label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_class_modal_course_field`)}
						name="courseId"
						options={searchFormCourseNamesOptions?.options || []}
						defaultValue={currentClass?.courseId}
					/>
					<Select
						label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_class_modal_teacher_field`)}
						name="classTeacherUserId"
						options={courseTeachersOptions?.options || []}
						defaultValue={currentClass?.classTeacherUserId}
					/>
					<DatePicker
						startDateDefaultValue={currentClass?.classFromDate && moment(currentClass?.classFromDate, 'DD-MM-YYYY').format('YYYY-MM-DD')}
						endDateDefaultValue={currentClass?.classToDate && moment(currentClass?.classToDate, 'DD-MM-YYYY').format('YYYY-MM-DD')}
						fromTo
						startDateName="fromDate"
						endDateName="toDate"
						label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_class_modal_date_field`)}
					/>
					<Button title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_class_modal_button`)} endIcon="downloadIcon" small />
				</form>
			</Modal>

			<Modal isVisible={studentModal} setVisibility={_handleCloseStudentModal} extraClassName="student_modal">
				<div className="columns">
					<MultiSelect
						bankTitle={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_student_modal_students_field_bank_title`)}
						selectedTitle={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_student_modal_students_field_selected_title`)}
						dataBank={studentsBank}
						selectedDataBank={studentsInClass}
						onChange={setStudentsInClass}
					/>
					<form onSubmit={_handleUpdateStudentInClass}>
						<TextArea
							label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_student_modal_message_field`)}
							name="messageToStudents"
							defaultValue={currentClass?.meetingRemark || ''}
						/>
						<div className="ClassModal__wrapper__checkbox">
							<input type="checkbox" name="didSendMassage" id="didSendMassage" disabled={popupDidSendMassageButtonDisabled}></input>
							<label htmlFor="didSendMassage">
								{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_student_modal_send_checkbox_field`)}
							</label>
						</div>
						<Button
							title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_student_modal_button`)}
							small
							extraClassName="student_modal__save_button"
						/>
					</form>
				</div>
			</Modal>

			<Modal isVisible={appointmentsModal} setVisibility={_handleCloseAppointmentsModal} extraClassName="appointments_modal">
				<div className="calendar_wrapper">
					<Calendar
						onDayChange={date => setCurrentDate(moment(date))}
						markedDates={calendarMeetings?.map(meeting => meeting.meetingDateTimeFrom) || []}
					/>
					<div className="schedule_appointment">
						<div className="appointments_list">{calendarMeetings?.map(_renderAppointment)}</div>
						<form className="appointments_form" onSubmit={_handleAddLocalClassMeeting}>
							<DatePicker
								type="time"
								fromTo
								startDateName="startTime"
								endDateName="endTime"
								label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_appointments_modal_date_field`)}
							/>
							<TextArea
								label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_appointments_modal_remark_field`)}
								name="meetingRemark"
							/>
							<Button
								extraClassName="appointments_form__button"
								title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_appointments_modal_add_button`)}
								small
								disabled={upcomingAppointments?.length >= 8}
							/>
						</form>
					</div>
				</div>
				<div className="ClassModal__table">
					{upcomingAppointments?.length > 0 && <Table columns={upcomingAppointmentsTableHeaders} data={upcomingAppointments} />}
					<div className="ClassModal__table__btn_container">
						{upcomingAppointments?.length > 0 && (
							<Button
								title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_appointments_modal_save_button`)}
								small
								onClick={_handleInsertMeetings}
								disabled={upcomingAppointments.every(app => app.meetingId)}
							/>
						)}
						<Checkbox
							description={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_appointments_modal_send_update_checkbox`)}
							name="sendMeetingNotification"
							onChange={({ target }) => setSendClassUpdate(target.checked)}
							checked={sendClassUpdate}
						/>
					</div>
				</div>
			</Modal>
		</div>
	);
};

export default ClassesManagement;
